import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Paper, 
  Grid, 
  Button,
  Box,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import ViewDayIcon from '@mui/icons-material/ViewDay'; // Icon for Navbar
import PreviewIcon from '@mui/icons-material/Preview';
import { useNavigate } from 'react-router-dom';
import { getSectionContent } from '../services/contentService';
import LivePreview from '../components/LivePreview';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [heroData, setHeroData] = useState(null);
  const [aboutUsData, setAboutUsData] = useState(null);
  const [servicesData, setServicesData] = useState(null);
  const [navbarData, setNavbarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeSection, setActiveSection] = useState('hero');
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch all section data
    const fetchAllSectionData = async () => {
      setLoading(true);
      try {
        // Fetch hero section data
        const heroData = await getSectionContent('heroSection');
        setHeroData(heroData);
        
        // Fetch about us section data
        const aboutUsData = await getSectionContent('aboutUsSection');
        setAboutUsData(aboutUsData);
        
        // Fetch services section data
        const servicesData = await getSectionContent('servicesSection');
        setServicesData(servicesData);
        

        // Fetch navbar section data
        const navbarData = await getSectionContent('navbarSection');
        setNavbarData(navbarData);
      } catch (error) {
        console.error('Error fetching section data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllSectionData();
  }, [refreshTrigger]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Navigation functions for section editors
  const goToHeroEditor = () => navigate('/dashboard/hero-section');
  const goToAboutUsEditor = () => navigate('/dashboard/about-us');
  const goToServicesEditor = () => navigate('/dashboard/services');
  const goToNavbarEditor = () => navigate('/dashboard/navbar-settings');

  // Handle tab change
  const handleTabChange = (event, newTabValue) => {
    setActiveTab(newTabValue);
    // Determine which section to show based on the tab
    switch (newTabValue) {
      case 0: setActiveSection('hero'); break;
      case 1: setActiveSection('about'); break;
      case 2: setActiveSection('services'); break;
      case 3: setActiveSection('navbar'); break; 
      case 4: setActiveSection(''); break;      
      default: setActiveSection('');
    }
  };

  // Refresh the preview
  const refreshPreview = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Welcome to the website content management system. You can manage all sections of your website and see live previews of your changes.
      </Typography>

      {/* Live Preview Section */}
      <LivePreview section={activeSection} refreshTrigger={refreshTrigger} />
      
      {/* Section Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="website sections">
          <Tab icon={<HomeIcon />} label="Hero" />
          <Tab icon={<InfoIcon />} label="About Us" />
          <Tab icon={<DesignServicesIcon />} label="Services" />
          <Tab icon={<ViewDayIcon />} label="Navbar" />
          <Tab icon={<PreviewIcon />} label="Full Page" />
        </Tabs>
      </Box>
      
      {/* Hero Section Summary */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ 
              backgroundColor: 'primary.light', 
              borderRadius: '50%', 
              p: 1,
              mr: 2
            }}>
              <HomeIcon color="primary" />
            </Box>
            <Typography variant="h6" component="div">
              Hero Section
            </Typography>
          </Box>
          
          {loading ? (
            <Typography variant="body2">Loading hero section data...</Typography>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" fontWeight="bold">Title:</Typography>
                <Typography variant="body2" paragraph>{heroData?.title || 'Not set'}</Typography>
                
                <Typography variant="body1" fontWeight="bold">Subtitle:</Typography>
                <Typography variant="body2" paragraph>{heroData?.subtitle || 'Not set'}</Typography>
                
                <Typography variant="body1" fontWeight="bold">Last Updated:</Typography>
                <Typography variant="body2" paragraph>
                  {heroData?.updatedAt ? formatDate(heroData.updatedAt) : 'Invalid Date'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body1" fontWeight="bold">Main Image:</Typography>
                {heroData?.mainImageUrl ? (
                  <Box sx={{ mt: 1, mb: 2, maxWidth: '200px' }}>
                    <img 
                      src={heroData.mainImageUrl} 
                      alt={heroData.mainImageAlt || 'Hero image'} 
                      style={{ width: '100%', height: 'auto', borderRadius: '4px' }} 
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" paragraph>No image set</Typography>
                )}
                
                <Typography variant="body1" fontWeight="bold">Show Brand Logos:</Typography>
                <Typography variant="body2" paragraph>
                  {heroData?.showBrandLogos ? 'Yes' : 'No'}
                </Typography>
              </Grid>
            </Grid>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={refreshPreview}
            >
              Refresh Preview
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={goToHeroEditor}
              startIcon={<HomeIcon />}
            >
              Edit Hero Section
            </Button>
          </Box>
        </Paper>
      )}

      {/* Navbar Section Summary */}
      {activeTab === 4 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ backgroundColor: 'secondary.light', borderRadius: '50%', p: 1, mr: 2 }}>
              <ViewDayIcon color="secondary" />
            </Box>
            <Typography variant="h6" component="div">
              Navbar Settings
            </Typography>
          </Box>
          {loading ? (
            <Typography variant="body2">Loading navbar settings data...</Typography>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" fontWeight="bold">Background Color:</Typography>
                <Typography variant="body2" paragraph sx={{ display: 'flex', alignItems: 'center' }}>
                  {navbarData?.backgroundColor || 'Not set'}
                  {navbarData?.backgroundColor && 
                    <Box component="span" sx={{ width: 20, height: 20, backgroundColor: navbarData.backgroundColor, ml: 1, border: '1px solid #ccc' }} />
                  }
                </Typography>
                
                <Typography variant="body1" fontWeight="bold">Link Color:</Typography>
                <Typography variant="body2" paragraph sx={{ display: 'flex', alignItems: 'center' }}>
                  {navbarData?.linkColor || 'Not set'}
                  {navbarData?.linkColor && 
                    <Box component="span" sx={{ width: 20, height: 20, backgroundColor: navbarData.linkColor, ml: 1, border: '1px solid #ccc' }} />
                  }
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" fontWeight="bold">Logo URL:</Typography>
                <Typography variant="body2" paragraph>{navbarData?.logoUrl || 'Not set'}</Typography>
                
                <Typography variant="body1" fontWeight="bold">Logo Max Height:</Typography>
                <Typography variant="body2" paragraph>{navbarData?.logoMaxHeight || 'Not set'}</Typography>

                <Typography variant="body1" fontWeight="bold">Last Updated:</Typography>
                <Typography variant="body2" paragraph>
                  {navbarData?.updatedAt ? formatDate(navbarData.updatedAt) : (navbarData ? 'Default settings active' : 'Not set')}
                </Typography>
              </Grid>
            </Grid>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button variant="outlined" color="primary" onClick={refreshPreview}>Refresh Preview</Button>
            <Button variant="contained" color="secondary" onClick={goToNavbarEditor} startIcon={<ViewDayIcon />}>
              Edit Navbar Settings
            </Button>
          </Box>
        </Paper>
      )}

      {/* About Us Section Summary */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ 
              backgroundColor: 'primary.light', 
              borderRadius: '50%', 
              p: 1,
              mr: 2
            }}>
              <InfoIcon color="primary" />
            </Box>
            <Typography variant="h6" component="div">
              About Us Section
            </Typography>
          </Box>
          
          {loading ? (
            <Typography variant="body2">Loading about us data...</Typography>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" fontWeight="bold">Main Title:</Typography>
                <Typography variant="body2" paragraph>{aboutUsData?.mainTitle || 'Not set'}</Typography>
                
                <Typography variant="body1" fontWeight="bold">Main Subtitle:</Typography>
                <Typography variant="body2" paragraph>{aboutUsData?.mainSubtitle || 'Not set'}</Typography>
                
                <Typography variant="body1" fontWeight="bold">Story Title:</Typography>
                <Typography variant="body2" paragraph>{aboutUsData?.storyTitle || 'Not set'}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body1" fontWeight="bold">Carousel Images:</Typography>
                <Typography variant="body2" paragraph>
                  {aboutUsData?.carouselImages ? `${aboutUsData.carouselImages.length} images` : 'No images'}
                </Typography>
                
                <Typography variant="body1" fontWeight="bold">Partner Logos:</Typography>
                <Typography variant="body2" paragraph>
                  {aboutUsData?.partnerLogos ? `${aboutUsData.partnerLogos.filter(logo => logo.isVisible).length} visible logos` : 'No logos'}
                </Typography>
                
                <Typography variant="body1" fontWeight="bold">Last Updated:</Typography>
                <Typography variant="body2" paragraph>
                  {aboutUsData?.updatedAt ? formatDate(aboutUsData.updatedAt) : 'Never'}
                </Typography>
              </Grid>
            </Grid>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={refreshPreview}
            >
              Refresh Preview
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={goToAboutUsEditor}
              startIcon={<InfoIcon />}
            >
              Edit About Us Section
            </Button>
          </Box>
        </Paper>
      )}

      {/* Services Section Summary */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ 
              backgroundColor: 'primary.light', 
              borderRadius: '50%', 
              p: 1,
              mr: 2
            }}>
              <DesignServicesIcon color="primary" />
            </Box>
            <Typography variant="h6" component="div">
              Services Section
            </Typography>
          </Box>
          
          {loading ? (
            <Typography variant="body2">Loading services data...</Typography>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" fontWeight="bold">Section Title:</Typography>
                <Typography variant="body2" paragraph>{servicesData?.title || 'Not set'}</Typography>
                
                <Typography variant="body1" fontWeight="bold">Number of Services:</Typography>
                <Typography variant="body2" paragraph>
                  {servicesData?.services ? servicesData.services.length : 0} services
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body1" fontWeight="bold">Long Service Card Title:</Typography>
                <Typography variant="body2" paragraph>
                  {servicesData?.longServiceCard?.title || 'Not configured'}
                </Typography>
                
                <Typography variant="body1" fontWeight="bold">Last Updated:</Typography>
                <Typography variant="body2" paragraph>
                  {servicesData?.updatedAt ? formatDate(servicesData.updatedAt) : 'Never'}
                </Typography>
              </Grid>
            </Grid>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={refreshPreview}
            >
              Refresh Preview
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={goToServicesEditor}
              startIcon={<DesignServicesIcon />}
            >
              Edit Services Section
            </Button>
          </Box>
        </Paper>
      )}

      {/* Full Page Preview */}
      {activeTab === 4 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ 
              backgroundColor: 'primary.light', 
              borderRadius: '50%', 
              p: 1,
              mr: 2
            }}>
              <PreviewIcon color="primary" />
            </Box>
            <Typography variant="h6" component="div">
              Full Website Preview
            </Typography>
          </Box>
          
          <Typography variant="body2" paragraph>
            This is a full preview of your website. Any changes you make in the admin panel will be reflected here after refreshing.
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={refreshPreview}
            >
              Refresh Full Preview
            </Button>
          </Box>
        </Paper>
      )}
    </div>
  );
};

export default Dashboard;
